import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GetImage } from '../helpers/getImage';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  constructor(
    private _api: ApiService,
    private getImage: GetImage,
  ) {}

  getOrganizations(query: string, page: number, perPage: number, sort: string, ascending: boolean, handler = ''): Observable<any> {
    const results = this._api
      .get(
        `/organization?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}&field=${name}${handler ? `&handler=${handler}` : ''}`,
      )
      .pipe(
        map((data: any) => ({
          ...data,
          items: data.items?.map((org) => {
            if (this.isActiveUserAdmin() ? org.name !== 'Default Org' : true) {
              this.getImage.organization(org.id).subscribe((img) => {
                org.img = img;
              });
            }
            return org;
          }),
        })),
      );
    return results;
  }

  getOrganizationByUriLabel(uriLabel: string): Observable<any> {
    const results = this._api.get(`/organization?query=uriLabel:${uriLabel}`).pipe(map((data) => data));
    return results;
  }

  getOrganizationById(id: string): Observable<any> {
    return this._api.get(`/organization/${id}`).pipe(
      map((organization: any) => {
        organization.nick = organization.name?.substring(0, 2).toUpperCase();

        this.getImage.organization(organization.id).subscribe((data) => {
          organization.image = data;
        });
        return organization;
      }),
    );
  }

  deleteOrganization(id: string, cascade: boolean): Observable<any> {
    const results = this._api.delete(`/organization/${id}?cascade=${cascade}`).pipe(map((data) => data));
    return results;
  }

  addOrganization(name: string, description: string, website: string, email: string, uriLabel: string): Observable<any> {
    const results = this._api
      .post(`/organization`, {
        name,
        description,
        website,
        email,
        uriLabel,
      })
      .pipe(map((data) => data));
    return results;
  }

  checkOrganizationDuplicate(organizationName?: string, uriLabel?: string, organizationId?: string): Observable<any> {
    const orgId = organizationId ? `&organizationId=${organizationId}` : null;
    const orgName = organizationName ? `&organizationName=${encodeURIComponent(organizationName)}` : null;
    const orgUriLabel = uriLabel ? `&uriLabel=${uriLabel}` : null;

    const results = this._api
      .post(`/organization/check?${orgName ? orgName : ''}${orgUriLabel ? orgUriLabel : ''}${orgId ? orgId : ''}`, {})
      .pipe(map((data) => data));
    return results;
  }

  updateOrganization(
    id: string,
    name: string,
    description: string,
    website: string,
    email: string,
    uriLabel: string,
    plan: string,
    planExpiration: string,
    planStart: string,
  ): Observable<any> {
    const results = this._api
      .patch(`/organization/${id}`, {
        id: id,
        name: name,
        description: description,
        website: website,
        email: email,
        uriLabel: uriLabel,
        ...(plan ? { plan } : null),
        planExpiration,
        planStart,
      })
      .pipe(map((data) => data));
    return results;
  }

  addUserToOrganization(orgId: string, userId: string, role: string): Observable<any> {
    const results = this._api.post(`/organization/${orgId}/${role.toLowerCase()}`, userId).pipe(map((data) => data));
    return results;
  }

  removeUserFromOrganization(org: any, userId: string, cascade: Boolean = true): Observable<any> {
    let role: string;
    let owners: string[] = [];
    owners = org.owners;
    if (owners.indexOf(userId) >= 0) {
      role = 'owner';
    } else {
      role = 'member';
    }

    const results = this._api.delete(`/organization/${org.id}/${role}/${userId}?cascade=${cascade}`).pipe(map((data) => data));
    return results;
  }

  deleteOrganizationImage(id: string): Observable<any> {
    return this._api.deleteIcon(`/organization/${id}/icon`).pipe(map((data) => data));
  }

  changeOrganizationUserRole(organizationId: string, currentRole: string, userId: string, newRole: string): Observable<any> {
    return this._api.put(`/organization/${organizationId}/${currentRole}/${userId}`, newRole).pipe(map((data) => data));
  }

  isActiveUserAdmin(): boolean {
    let activeUser: any = localStorage.getItem('user_data');
    activeUser = JSON.parse(activeUser);
    return activeUser?.role?.toLowerCase() === 'admin';
  }

  //license endpoints

  getOrganizationUserLicenses(userId: string, type: string): Observable<any> {
    return this._api.get(`/license/organization?userId=${userId}&licenseType=${type}`).pipe(map((data) => data));
  }

  getOrganizationLicenses(organizationId: string): Observable<any> {
    return this._api
      .get(`/license?organizationId=${organizationId}&query=organizationId:${organizationId}&limit=1000&ascending=true`)
      .pipe(map((data) => data));
  }

  getLicense(query?: string, offset?: number, limit?: number, sort?: string, ascending = null): Observable<any> {
    return this._api
      .get(`/license?query=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}&sort=${sort}&ascending=${ascending}`)
      .pipe(map((data) => data));
  }

  getLicenseTextById(id: string): Observable<any> {
    return this._api.getResourceLicenses(`/config/license/${id}/text`).pipe(map((data) => data));
  }

  addLicenseToOrganization(license: string, orgId: string): Observable<any> {
    return this._api.post(`/license/accept?licenseInfoType=${license}&organizationId=${orgId}`, {}).pipe(map((data) => data));
  }

  removeLicenseFromOrganization(id: string): Observable<any> {
    const results = this._api.delete(`/license/${id}`).pipe(map((data) => data));
    return results;
  }

  addLicenseToOrgAdmin(license: string, orgId: string): Observable<any> {
    return this._api.post(`/license`, { type: license, organizationId: orgId }).pipe(map((data) => data));
  }
}
